import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState, useEffect } from "react";
import { Collapse } from "react-bootstrap";
import { Title } from "@components/ui";
import BorderedContainer from "@components/ui/bordered_container";
import classNames from "classnames";
import { FlexContainer } from "@components/ui/flex_container";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { THEME_COLOR } from "@utils/constants";
import { Tooltip as ReactTooltip } from "react-tooltip";

export default function InfoSection({
    hidden,
    title,
    subtitle,
    children,
    collapsible,
    collapsed,
    titleSize,
    icon,
    items,
    renderItem,
    horizontal,
    style,
    containerStyle,
    complementaryComponent,
    titleTextColor = "#000000",
    className,
    containerClassName,
    disabledOverlay,
    disabledOverlayReason,
    gap,
    titleContainerStyle,
    onItemsReordered = () => {},
    draggable,
    ...props
}) {
    const [isCollapsed, setIsCollapsed] = useState(collapsed);
    const [currentTheme, setCurrentTheme] = useState("light");
    const [dynamicTitleColor, setDynamicTitleColor] = useState(titleTextColor);

    // Fylgist með þemu breytingum
    useEffect(() => {
        // Frumstilla með núverandi þemu
        const dataTheme = document.documentElement.getAttribute("data-theme") || "light";
        setCurrentTheme(dataTheme);

        // Búa til MutationObserver til að fylgjast með þemu breytingum
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === "attributes" && mutation.attributeName === "data-theme") {
                    const newTheme = document.documentElement.getAttribute("data-theme") || "light";
                    setCurrentTheme(newTheme);
                }
            });
        });

        observer.observe(document.documentElement, { attributes: true });

        return () => observer.disconnect();
    }, []);

    // Uppfæra titil litinn þegar þemað breytist
    useEffect(() => {
        // Ef sérstakur litur er sendur í gegnum props, notum hann alltaf
        if (titleTextColor !== "#000000") {
            setDynamicTitleColor(titleTextColor);
            return;
        }

        // Annars veljum við sjálfkrafa lit eftir þema
        if (currentTheme === "dark") {
            setDynamicTitleColor("#e0e0e0"); // Ljós texti fyrir myrkt þema
        } else {
            setDynamicTitleColor("#000000"); // Dökkur texti fyrir ljóst þema
        }
    }, [currentTheme, titleTextColor]);

    // Function to update list on drop
    const handleDrop = (droppedItem) => {
        // Ignore drop outside droppable container or if items is undefined
        if (!droppedItem.destination || !items) return;
        let updatedList = [...items];
        // Remove dragged item
        const [reorderedItem] = updatedList.splice(droppedItem.source.index, 1);
        // Add dropped item
        updatedList.splice(droppedItem.destination.index, 0, reorderedItem);
        // Update State
        onItemsReordered(updatedList);
    };

    if (hidden) {
        return <></>;
    }

    return (
        <div
            {...props}
            style={{
                marginBottom: 20,
                ...style,
            }}
            className={className}
        >
            {disabledOverlay && (
                <ReactTooltip
                    place="top"
                    id="tooltip-info-is"
                    style={{
                        backgroundColor: THEME_COLOR,
                        borderRadius: 900,
                        color: "white",
                        padding: "5px 15px",
                        boxShadow: "0 0 5px #0000000a",
                        zIndex: 49,
                    }}
                />
            )}
            <div
                className="d-flex flex-row align-items-end justify-content-between flex-wrap"
                style={{ cursor: collapsible ? "pointer" : null, ...titleContainerStyle }}
                onClick={() => {
                    setIsCollapsed(!isCollapsed);
                }}
            >
                <div className="d-flex flex-row">
                    <div className="d-flex" style={{ alignItems: "center" }}>
                        <span className="align-text-bottom">
                            {collapsible &&
                                (isCollapsed ? (
                                    <FontAwesomeIcon
                                        icon={faChevronDown}
                                        color={dynamicTitleColor}
                                    />
                                ) : (
                                    <FontAwesomeIcon icon={faChevronUp} color={dynamicTitleColor} />
                                ))}
                        </span>
                    </div>

                    {icon && (
                        typeof icon === "function" ? (
                            React.createElement(icon, { style: { width: 30, height: 30, marginRight: 5 } })
                        ) : (
                            <img src={icon} style={{ width: 30, height: 30, marginRight: 5 }}></img>
                        )
                    )}
                    <FlexContainer column>
                        <Title
                            size={titleSize ? titleSize : 5}
                            style={{
                                margin: 0,
                                paddingLeft: 10,
                                color: dynamicTitleColor,
                            }}
                            className="company-location-group-title"
                        >
                            {title}
                        </Title>
                        {subtitle && (
                            <Title
                                size={6}
                                style={{
                                    margin: 0,
                                    paddingLeft: 10,
                                    color: dynamicTitleColor + "aa",
                                    fontWeight: 400,
                                }}
                                className="company-location-group-title"
                            >
                                {subtitle}
                            </Title>
                        )}
                    </FlexContainer>
                </div>
                <div
                    style={{
                        display: "flex",
                        marginTop: 5,
                        flexGrow: 1,
                        justifyContent: "flex-end",
                    }}
                >
                    {complementaryComponent}
                </div>
            </div>
            <Collapse in={!collapsible || !isCollapsed}>
                <div>
                    {items && renderItem && (
                        <RenderItems
                            items={items}
                            renderItem={renderItem}
                            handleDrop={handleDrop}
                            draggable={draggable}
                            horizontal={horizontal}
                            gap={gap}
                        />
                    )}
                    {children && (
                        <RenderChildren
                            containerClassName={containerClassName}
                            containerStyle={containerStyle}
                            disabledOverlay={disabledOverlay}
                            disabledOverlayReason={disabledOverlayReason}
                            currentTheme={currentTheme}
                        >
                            {children}
                        </RenderChildren>
                    )}
                </div>
            </Collapse>
        </div>
    );
}

const RenderItems = ({ items, handleDrop, draggable, renderItem, horizontal, gap }) => {
    if (!items || items.length === 0) return null;

    if (horizontal) {
        return (
            <FlexContainer style={{ flexWrap: "wrap", gap: gap, flexGrow: 1 }}>
                {items.map((item, index) => {
                    return renderItem(item, index);
                })}
            </FlexContainer>
        );
    }
    return (
        <DragDropContext onDragEnd={handleDrop}>
            <Droppable droppableId="dropId">
                {(provided) => (
                    <>
                        <div {...provided.droppableProps} ref={provided.innerRef}>
                            <div>
                                {items.map((item, index) => {
                                    if (!item.draggableId) {
                                        console.warn("Item missing draggableId:", item);
                                    }
                                    return (
                                        <Draggable
                                            key={item.draggableId}
                                            draggableId={item.draggableId}
                                            index={index}
                                            isDragDisabled={!draggable}
                                        >
                                            {(provided, snapshot) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                >
                                                    {renderItem(item, index, snapshot)}
                                                </div>
                                            )}
                                        </Draggable>
                                    );
                                })}
                            </div>
                        </div>
                        {provided.placeholder}
                    </>
                )}
            </Droppable>
        </DragDropContext>
    );
};

const RenderChildren = ({
    containerClassName,
    containerStyle,
    disabledOverlay,
    disabledOverlayReason,
    children,
    currentTheme,
}) => {
    return (
        <BorderedContainer
            className={classNames(containerClassName, "bordered-container-small-sections")}
            style={{ position: "relative", ...containerStyle }}
        >
            {disabledOverlay && (
                <div
                    data-tooltip-id={"tooltip-info-is"}
                    data-tooltip-content={disabledOverlayReason}
                    style={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        backgroundColor:
                            currentTheme === "dark"
                                ? "rgba(0, 0, 0, 0.6)"
                                : "rgba(255, 255, 255, 0.6)",
                        borderRadius: 8,
                        zIndex: 48,
                        top: 0,
                        left: 0,
                    }}
                ></div>
            )}
            {children}
        </BorderedContainer>
    );
};
